import { useMemo } from "react";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { zeroErrorMessage } from "@/lib/zero-error";
import type {
  CrewPlanningAvailability,
  CrewPlanningPerson,
  CrewPlanningProject,
} from "@/lib/crew-planning";
import type { AvailabilityStatus } from "@/hooks/useProjectPeople";

type ZeroCrewPlanningRow = {
  id: string;
  projectID: string;
  personID: string;
  status: AvailabilityStatus;
  availableDays?: (number | string)[] | null;
  notes?: string | null;
  person?: {
    id: string;
    firstName: string;
    lastName: string;
    personType?: string | null;
  } | null;
  project?: {
    id: string;
    name: string;
    planningStartDate?: number | string | null;
    planningEndDate?: number | string | null;
    eventStartDate?: number | string | null;
    eventEndDate?: number | string | null;
    startDate?: number | string | null;
    endDate?: number | string | null;
  } | null;
};

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toLegacyDateString(value: number | string) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value;
}

function toLegacyPerson(
  person: ZeroCrewPlanningRow["person"],
): CrewPlanningPerson | null {
  if (!person) return null;
  return {
    id: person.id,
    first_name: person.firstName,
    last_name: person.lastName,
    person_type: person.personType ?? null,
  };
}

function toLegacyProject(
  project: ZeroCrewPlanningRow["project"],
): CrewPlanningProject | null {
  if (!project) return null;
  return {
    id: project.id,
    name: project.name,
    planning_start_date: toLegacyDate(project.planningStartDate),
    planning_end_date: toLegacyDate(project.planningEndDate),
    event_start_date: toLegacyDate(project.eventStartDate),
    event_end_date: toLegacyDate(project.eventEndDate),
    start_date: toLegacyDate(project.startDate),
    end_date: toLegacyDate(project.endDate),
  };
}

function toLegacyCrewPlanningAvailability(
  row: ZeroCrewPlanningRow,
): CrewPlanningAvailability {
  return {
    id: row.id,
    project_id: row.projectID,
    person_id: row.personID,
    status: row.status,
    available_days: (row.availableDays ?? []).map(toLegacyDateString),
    notes: row.notes ?? null,
    person: toLegacyPerson(row.person),
    project: toLegacyProject(row.project),
  };
}

export function useCrewPlanningAvailability() {
  const [data, details] = useZeroQuery(
    appQueries.projectAvailability.pendingPlanning(),
  );

  const availability = useMemo(
    () =>
      ((data ?? []) as ZeroCrewPlanningRow[]).map(
        toLegacyCrewPlanningAvailability,
      ),
    [data],
  );

  return {
    data: availability,
    isLoading: details.type !== "complete" && availability.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
